import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { FBPageWithInsta } from "../../../../api/social-account-connect/instagram";
import { FBPageExtended } from "../../../../api/social-account-connect/facebook";
import { YoutubeChannel } from "../../../../api/social-account-connect/youtube";
import { getAccountPlatformType } from "../../../../entities/accounts/libs/account-platform";
import {
  findExistingInstagramAccount,
  resolveInstagramReconnectAccountId,
} from "./resolve-instagram-reconnect";
import { isFacebookTargetAlreadyAdded } from "./resolve-facebook-already-added";
import { GmbLocation } from "../../../../api/social-account-connect/google";
import { LinkedInProfile } from "../../../../api/social-account-connect/linkedin";
import { getUser } from "../../../../selectors/commonSelectors";

import { SocialAccountType } from "../../SocialAccountType";
import { InstagramConnectDialog } from "../instagram-connect-dialog";
import { YoutubeConnectDialog } from "../youtube-connect-dialog";
import { FacebookPageConnectDialog } from "../facebook-page-connect-dialog";
import { GoogleMyBusinessConnectDialog } from "../google-my-business-connect-dialog";
import { LinkedinConnectDialog } from "../linkedin-connect-dialog";
import { AddPasswordDialog } from "../../../instagram-actions/components/add-password-dialog";

import {
  closeDialog,
  closeHowdyGoDemoDialog,
  disableMainDialogGuard,
  openDialog,
  openHowdyGoDemoDialog,
  openSocialAccountConnectDialog,
  startConnectingAccountFx,
  enableMainDialogGuard,
} from "./model";
import { NotificationAccountConnectDialog } from "../notification-account-connect-dialog";
import { TikTokConnected } from "../connected-modals";
import { useSocialAccountConnectQueryParams } from "../../SocialAccountConnectQueryParams";
import { PlatformNameToPlatformTypeMap } from "../../../post/format-post";
import { NewSocialAccountConnectDialog } from "../social-account-connect-dialog/new-social-account-connect-dialog";
import { ThreadsConnectedModal } from "../connected-modals/threads-connected-modal";
import {
  HowdyGoDemoModal,
  resolveHowdyGoContext,
  isValidTourId,
} from "./howdygo-demo-modal";
import { useLocation } from "react-router-dom";
import {
  selectSelectedConnectedAccount,
  selectSocialAccountConnectView,
} from "../../redux-model";
import { isSocialSetOnly } from "../../../../utils/social-set";
import { getSocialSetUsedSlots } from "../../../../selectors/skedCoreSelectors";

const AUTO_OPEN_EXCLUDED_PATH_PREFIXES = [
  "/dashboard/accounts",
  "/dashboard/groups",
  "/dashboard/profile",
  "/dashboard/subscription",
  "/dashboard/collaborate",
  "/dashboard/collaboration",
  "/dashboard/change-password",
  "/dashboard/invoices",
  "/dashboard/referral",
  "/dashboard/users",
];

function matchesPathPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function normalizePathname(pathname: string): string {
  const normalizedPathname = pathname.toLowerCase().replace(/\/+$/, "");

  return normalizedPathname || "/";
}

function isHomeOnboardingPath(pathname: string): boolean {
  return (
    pathname === "/dashboard" ||
    matchesPathPrefix(pathname, "/dashboard/onboarding")
  );
}

export function shouldAutoOpenSocialAccountConnectDialog(
  pathname: string
): boolean {
  const normalizedPathname = normalizePathname(pathname);

  if (!matchesPathPrefix(normalizedPathname, "/dashboard")) {
    return false;
  }

  if (isHomeOnboardingPath(normalizedPathname)) {
    return false;
  }

  if (
    AUTO_OPEN_EXCLUDED_PATH_PREFIXES.some((prefix) =>
      matchesPathPrefix(normalizedPathname, prefix)
    )
  ) {
    return false;
  }

  return resolveHowdyGoContext(normalizedPathname) === null;
}

export function SocialAccountConnect() {
  const {
    currentDialog,
    isLoading,
    existingAccounts,
    counters,
    showHowdyGoDemoDialog,
    isMainDialogGuarded,
  } = useSelector(selectSocialAccountConnectView);
  const user = useSelector(getUser);
  const usedSocialSetSlots = useSelector(getSocialSetUsedSlots);
  const inSocialSetMode = isSocialSetOnly(user);
  // Only forward `usedSlots` to the dialog when the user is actually on the
  // social-set-only entitlement — the dialog treats `socialSetMode === undefined`
  // as "not in social-set mode", so we don't need a redundant `enabled` flag.
  const socialSetMode = useMemo(
    () => (inSocialSetMode ? { usedSlots: usedSocialSetSlots } : undefined),
    [inSocialSetMode, usedSocialSetSlots]
  );
  const [params, setParams] = useSocialAccountConnectQueryParams();
  const location = useLocation();
  const autoOpenedPathRef = useRef<string | null>(null);
  const selectedAccount = useSelector(selectSelectedConnectedAccount);

  const onConnectAccount = (account: SocialAccountType) => {
    startConnectingAccountFx({
      acc_type: account,
      preferences: user?.preferences,
    });
  };

  const isTTConnected =
    params?.reconnectedFromNotifications?.toLocaleUpperCase() ===
    PlatformNameToPlatformTypeMap.tiktok;
  const isTHConnected =
    params?.reconnectedFromNotifications?.toLocaleUpperCase() ===
    PlatformNameToPlatformTypeMap.threads;

  const hasLoadedUser = Boolean(user?.userId);
  const hasLoadedCounts =
    Boolean(counters?.pricingVersion) || existingAccounts.length > 0;
  const accountsReady = hasLoadedUser && hasLoadedCounts;
  const currentTourId =
    params?.tour && isValidTourId(params.tour) ? params.tour : null;
  const shouldAutoOpenMainDialog = useMemo(
    () => shouldAutoOpenSocialAccountConnectDialog(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    if (
      !accountsReady ||
      showHowdyGoDemoDialog ||
      currentTourId !== null ||
      !shouldAutoOpenMainDialog ||
      existingAccounts.length > 0 ||
      autoOpenedPathRef.current === location.pathname
    ) {
      return;
    }

    openSocialAccountConnectDialog();
    autoOpenedPathRef.current = location.pathname;
  }, [
    existingAccounts.length,
    accountsReady,
    currentTourId,
    location.pathname,
    shouldAutoOpenMainDialog,
    showHowdyGoDemoDialog,
  ]);

  useEffect(() => {
    if (params?.tour && currentTourId === null) {
      console.warn(`Invalid tour ID: ${params.tour}`);
      setParams({ tour: undefined }, "replaceIn");
    }
  }, [currentTourId, params?.tour, setParams]);

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const handleLaunchAddAccount = () => {
    enableMainDialogGuard();
    closeHowdyGoDemoDialog();
    openSocialAccountConnectDialog();
  };

  const handleMainDialogHide = () => {
    closeDialog();
    if (isMainDialogGuarded) {
      disableMainDialogGuard();
      openHowdyGoDemoDialog();
    }
  };

  const handleTourModalClose = () => {
    if (params?.tour) {
      setParams({ tour: undefined }, "replaceIn");
    }
  };

  return (
    <>
      {/* Tour modal (URL parameter triggered) */}
      {currentTourId && (
        <HowdyGoDemoModal
          mode="tour"
          show
          tourId={currentTourId}
          onHide={handleTourModalClose}
        />
      )}
      {/* Demo modal (auto-shows when no accounts) */}
      <HowdyGoDemoModal
        mode="demo"
        show={showHowdyGoDemoDialog}
        existingAccountsCount={existingAccounts.length}
        accountsReady={accountsReady}
        onLaunchAddAccount={handleLaunchAddAccount}
        isMainDialogGuarded={isMainDialogGuarded}
      />
      <NewSocialAccountConnectDialog
        counters={counters}
        show={currentDialog === "main"}
        onHide={handleMainDialogHide}
        isLocked={isMainDialogGuarded}
        isLoading={isLoading}
        onConnectAccount={onConnectAccount}
        socialSetMode={socialSetMode}
      />
      <InstagramConnectDialog
        show={currentDialog === SocialAccountType.Instagram}
        onHide={closeDialog}
        onBack={() => openDialog("main")}
        isAlreadyAdded={(account: FBPageWithInsta): boolean =>
          Boolean(findExistingInstagramAccount(account, existingAccounts))
        }
        /*
         * SKE-4716. An account that failed a publish with a `needs_reconnect`
         * error keeps `instagramPublishReconnectRequired` until it is genuinely
         * re-added, and stops routing to the API track meanwhile. It is still
         * "already added", so without this it rendered as an inert "Connected"
         * label with nothing to click, while the accounts page was telling the
         * same customer "Publishing: needs update".
         *
         * The id, not a boolean, because re-adding an existing account has to
         * target that account by id through `PUT /accounts/addIgToken/{id}`.
         */
        reconnectAccountId={(account: FBPageWithInsta): string | undefined =>
          resolveInstagramReconnectAccountId(account, existingAccounts)
        }
      />
      <YoutubeConnectDialog
        show={currentDialog === SocialAccountType.Youtube}
        onHide={closeDialog}
        onBack={() => openDialog("main")}
        isAlreadyAdded={(account: YoutubeChannel): boolean => {
          return existingAccounts.some(
            (acc) =>
              getAccountPlatformType(acc) === "YT" &&
              acc.login.toLowerCase() === account.login.toLowerCase()
          );
        }}
      />
      <FacebookPageConnectDialog
        show={currentDialog === SocialAccountType.FacebookPage}
        onHide={closeDialog}
        onBack={() => openDialog("main")}
        isAlreadyAdded={(page: FBPageExtended): boolean =>
          isFacebookTargetAlreadyAdded(page, existingAccounts)
        }
      />
      <GoogleMyBusinessConnectDialog
        show={currentDialog === SocialAccountType.GoogleMyBusiness}
        onHide={closeDialog}
        onBack={() => openDialog("main")}
        isAlreadyAdded={(location: GmbLocation): boolean => {
          // match on location.name only which is
          // the locationId, not on the full platformId
          // which includes the account ID and can change
          return existingAccounts.some(
            (acc) =>
              getAccountPlatformType(acc) === "GMB" &&
              acc.platformId?.includes(location.name)
          );
        }}
      />
      <LinkedinConnectDialog
        show={
          currentDialog === SocialAccountType.LinkedInCompany ||
          currentDialog === SocialAccountType.LinkedInPersonal
        }
        onHide={closeDialog}
        onBack={() => openDialog("main")}
        isAlreadyAdded={(profile: LinkedInProfile): boolean => {
          return existingAccounts.some((acc) => {
            const parsedPlatformId = Number.parseInt(
              acc?.platformId || "0",
              10
            );
            return (
              getAccountPlatformType(acc) === "LI" &&
              parsedPlatformId === profile.id
            );
          });
        }}
      />

      <NotificationAccountConnectDialog
        show={currentDialog === SocialAccountType.TikTok}
        type="tiktok"
        onHide={closeDialog}
        onBack={() => openDialog("main")}
      />
      <NotificationAccountConnectDialog
        show={currentDialog === SocialAccountType.ThreadsNotification}
        type="threads"
        onHide={closeDialog}
        onBack={() => openDialog("main")}
      />

      <NotificationAccountConnectDialog
        show={currentDialog === SocialAccountType.Snapchat}
        type="snapchat"
        onHide={closeDialog}
        onBack={() => openDialog("main")}
      />

      <NotificationAccountConnectDialog
        show={currentDialog === SocialAccountType.Other}
        type="other"
        onHide={closeDialog}
        onBack={() => openDialog("main")}
      />

      {/*  connected modals */}
      <TikTokConnected
        show={currentDialog === SocialAccountType.TikTok && isTTConnected}
        onHide={closeDialog}
      />
      <ThreadsConnectedModal
        show={
          currentDialog === SocialAccountType.ThreadsConnect && isTHConnected
        }
        onHide={closeDialog}
      />

      {selectedAccount ? (
        <AddPasswordDialog
          show={currentDialog === "add-password"}
          onHide={closeDialog}
          account={selectedAccount}
        />
      ) : null}
    </>
  );
}
